/**
 * Question Editor
 * Create or edit a diagnostic question
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useQuestion,
  useCreateQuestion,
  useUpdateQuestion,
  type QuestionInput,
} from '@/hooks/use-admin-diagnostic';
import { usePillars } from '@/hooks/use-pillars';

export const Route = createFileRoute('/admin/diagnostic/questions/$id')({
  component: QuestionEditor,
});

const questionSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  pillar_id: z.string().min(1, 'Pillar is required'),
  question_text: z.string().min(10, 'Question text must be at least 10 characters'),
  type: z.enum(['scale_5', 'scale_10', 'yesno', 'numeric', 'trend', 'select']),
  weight: z.number().min(0).max(10),
  routing: z.enum(['all', 'buildup+', 'scaleup+', 'systemise+']).optional(),
  display_order: z.number().int().min(0).optional(),
  help_text: z.string().optional(),
});

type QuestionFormData = z.infer<typeof questionSchema>;

interface ScoringRange {
  min: number;
  max: number;
  score: number;
}

function ScoringRangesEditor({
  ranges,
  onChange,
}: {
  ranges: ScoringRange[];
  onChange: (ranges: ScoringRange[]) => void;
}) {
  const addRow = () =>
    onChange([...ranges, { min: 0, max: 0, score: 0 }]);

  const removeRow = (i: number) =>
    onChange(ranges.filter((_, idx) => idx !== i));

  const updateRow = (i: number, field: keyof ScoringRange, raw: string) => {
    const value = raw === '' ? 0 : parseFloat(raw);
    if (isNaN(value)) return;
    const updated = ranges.map((r, idx) =>
      idx === i ? { ...r, [field]: value } : r,
    );
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Scoring Ranges</Label>
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <Plus className="w-3 h-3 mr-1" />
          Add range
        </Button>
      </div>

      {ranges.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          No ranges defined — question will always score 0.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-xs text-muted-foreground px-1">
            <span>Min</span>
            <span>Max</span>
            <span>Score (0–100)</span>
            <span />
          </div>
          {ranges.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
              <Input
                type="number"
                value={r.min}
                onChange={(e) => updateRow(i, 'min', e.target.value)}
                className="h-8 text-sm"
              />
              <Input
                type="number"
                value={r.max}
                onChange={(e) => updateRow(i, 'max', e.target.value)}
                className="h-8 text-sm"
              />
              <Input
                type="number"
                min={0}
                max={100}
                value={r.score}
                onChange={(e) => updateRow(i, 'score', e.target.value)}
                className="h-8 text-sm"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                onClick={() => removeRow(i)}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionEditor() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const isNew = id === 'new';

  const { data: question, isLoading } = useQuestion(isNew ? null : id);
  const { data: pillars = [], isLoading: pillarsLoading } = usePillars();
  const createQuestion = useCreateQuestion();
  const updateQuestion = useUpdateQuestion();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<QuestionFormData>({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      routing: 'all',
      display_order: 999,
    },
  });

  const questionType = watch('type');
  const [scoringRanges, setScoringRanges] = useState<ScoringRange[]>([]);

  // Populate form when editing existing question
  useEffect(() => {
    if (question) {
      setValue('code', question.code);
      setValue('pillar_id', question.pillar_id);
      setValue('question_text', question.question_text);
      setValue('type', question.type);
      setValue('weight', question.weight);
      setValue('routing', question.routing);
      setValue('display_order', question.display_order);
      setValue('help_text', question.help_text || '');

      if (question.type === 'numeric') {
        const existingRanges = question.scoring_rules?.ranges ?? [];
        setScoringRanges(existingRanges);
      }
    }
  }, [question, setValue]);

  // Reset ranges when switching away from/to numeric
  useEffect(() => {
    if (questionType !== 'numeric') setScoringRanges([]);
  }, [questionType]);

  const onSubmit = async (data: QuestionFormData) => {
    try {
      // Build scoring_rules based on question type
      let scoring_rules: Record<string, unknown> = {};

      switch (data.type) {
        case 'scale_5':
          scoring_rules = { formula: '(value / 5) * 100' };
          break;
        case 'scale_10':
          scoring_rules = { formula: '(value / 10) * 100' };
          break;
        case 'yesno':
          scoring_rules = { type: 'lookup', values: { yes: 100, no: 0 } };
          break;
        case 'numeric':
          scoring_rules = { type: 'ranges', ranges: scoringRanges };
          break;
        case 'trend':
          scoring_rules = { type: 'lookup', up: 100, flat: 50, down: 0 };
          break;
      }

      const input: QuestionInput = {
        ...data,
        scoring_rules,
        weight: Number(data.weight),
      };

      if (isNew) {
        await createQuestion.mutateAsync(input);
      } else {
        await updateQuestion.mutateAsync({ id, input });
      }

      navigate({ to: '/admin/diagnostic/questions' });
    } catch (error) {
      toast.error('Failed to save question');
    }
  };

  if (isLoading && !isNew) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/admin/diagnostic/questions' })}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div>
          <h1 className="text-3xl font-bold">
            {isNew ? 'New Question' : `Edit Question ${question?.code}`}
          </h1>
          {!isNew && (
            <p className="text-muted-foreground mt-1">Version {question?.version}</p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle>Question Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Code */}
            <div className="space-y-2">
              <Label htmlFor="code">Question Code *</Label>
              <Input
                id="code"
                {...register('code')}
                placeholder="e.g., F01, O05, M12"
                disabled={!isNew}
              />
              {errors.code && (
                <p className="text-sm text-destructive">{errors.code.message}</p>
              )}
            </div>

            {/* Pillar */}
            <div className="space-y-2">
              <Label htmlFor="pillar_id">Pillar *</Label>
              <Select
                key={watch('pillar_id') || 'no-pillar'}
                value={watch('pillar_id')}
                onValueChange={(value: string) => setValue('pillar_id', value)}
                disabled={pillarsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={pillarsLoading ? "Loading pillars..." : "Select pillar"} />
                </SelectTrigger>
                <SelectContent>
                  {pillars.map((pillar) => (
                    <SelectItem key={pillar.id} value={pillar.id}>
                      {pillar.name} ({pillar.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.pillar_id && (
                <p className="text-sm text-destructive">{errors.pillar_id.message}</p>
              )}
            </div>

            {/* Question Text */}
            <div className="space-y-2">
              <Label htmlFor="question_text">Question Text *</Label>
              <Textarea
                id="question_text"
                {...register('question_text')}
                placeholder="Enter the question text..."
                rows={4}
              />
              {errors.question_text && (
                <p className="text-sm text-destructive">{errors.question_text.message}</p>
              )}
            </div>

            {/* Type and Weight */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Question Type *</Label>
                <Select
                  value={watch('type')}
                  onValueChange={(value: any) => setValue('type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scale_5">Scale 1-5</SelectItem>
                    <SelectItem value="scale_10">Scale 1-10</SelectItem>
                    <SelectItem value="yesno">Yes/No</SelectItem>
                    <SelectItem value="numeric">Numeric</SelectItem>
                    <SelectItem value="trend">Trend (Up/Flat/Down)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="weight">Weight (0-10) *</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.01"
                  {...register('weight', { valueAsNumber: true })}
                  placeholder="1.0"
                />
                {errors.weight && (
                  <p className="text-sm text-destructive">{errors.weight.message}</p>
                )}
              </div>
            </div>

            {/* Routing and Display Order */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="routing">Routing</Label>
                <Select
                  value={watch('routing')}
                  onValueChange={(value: any) => setValue('routing', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select routing" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All (Default)</SelectItem>
                    <SelectItem value="buildup+">Buildup+</SelectItem>
                    <SelectItem value="scaleup+">Scale-up+</SelectItem>
                    <SelectItem value="systemise+">Systemise+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  {...register('display_order', { valueAsNumber: true })}
                  placeholder="999"
                />
              </div>
            </div>

            {/* Help Text */}
            <div className="space-y-2">
              <Label htmlFor="help_text">Help Text (Optional)</Label>
              <Textarea
                id="help_text"
                {...register('help_text')}
                placeholder="Additional guidance for users..."
                rows={2}
              />
            </div>

            {/* Scoring Rules */}
            {questionType === 'numeric' ? (
              <ScoringRangesEditor
                ranges={scoringRanges}
                onChange={setScoringRanges}
              />
            ) : questionType ? (
              <div className="p-4 bg-secondary rounded-lg">
                <h3 className="font-medium mb-1 text-sm">Scoring</h3>
                <p className="text-sm text-muted-foreground">
                  {questionType === 'scale_5' && 'Score = (answer / 5) × 100'}
                  {questionType === 'scale_10' && 'Score = (answer / 10) × 100'}
                  {questionType === 'yesno' && 'Yes = 100, No = 0'}
                  {questionType === 'trend' && 'Up = 100, Flat = 50, Down = 0'}
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ to: '/admin/diagnostic/questions' })}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createQuestion.isPending || updateQuestion.isPending}
          >
            {createQuestion.isPending || updateQuestion.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isNew ? 'Create Question' : 'Update Question'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
    </div>
  );
}
